/**
 * This carousel shows up inside the chat conversations with the AI assistant.
 * It is used to show the user product recommendations.
 */
import React, { useEffect, useRef, useState } from "react";

import {
  AdonisProductRecommendationCard,
  AdonisProductRecommendationCardProps,
} from "./AdonisProductRecommendationCard";

interface CarouselScrollIndicatorProps {
  itemCount: number;
  scrollContainerRef: React.RefObject<HTMLUListElement>;
}

const CarouselScrollIndicator = ({
  itemCount,
  scrollContainerRef,
}: CarouselScrollIndicatorProps) => {
  const dotsContainerRef = useRef<HTMLDivElement>(null);
  const backgroundBarRef = useRef<HTMLDivElement>(null);
  const [activeDotIndex, setActiveDotIndex] = useState(0);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const dotsContainer = dotsContainerRef.current;
    const backgroundBar = backgroundBarRef.current;

    if (!scrollContainer || !dotsContainer || !backgroundBar) {
      return;
    }

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;

      // Get the actual width of the dots container from the DOM
      const dotsContainerWidth = dotsContainer.getBoundingClientRect().width;

      // Card width is 195px, gap is 16px (gap-4 in Tailwind)
      const cardWidth = 195;
      const cardGap = 16;
      const cardWithGap = cardWidth + cardGap;

      // Calculate which card is the leftmost visible one
      const newActiveDot = Math.round(scrollLeft / cardWithGap);
      setActiveDotIndex(Math.min(newActiveDot, itemCount - 1));

      // Calculate visible proportion
      const visibleProportion = clientWidth / scrollWidth;

      // Calculate bar width based on visible proportion
      const barWidth = dotsContainerWidth * visibleProportion;

      // Calculate scroll progress (0 to 1)
      const maxScroll = scrollWidth - clientWidth;
      const scrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;

      // Calculate bar position
      const maxBarPosition = dotsContainerWidth - barWidth;
      const barPosition = scrollProgress * maxBarPosition;

      // Update background bar via direct DOM manipulation
      backgroundBar.style.width = `${barWidth}px`;
      backgroundBar.style.transform = `translateX(${barPosition}px)`;
    };

    // Initial call to set up the indicator
    handleScroll();

    // Attach scroll listener
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    // Optional: Handle resize events
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef, itemCount]);

  if (itemCount <= 1) {
    return null;
  }

  return (
    <div className="flex justify-center py-3">
      <div ref={dotsContainerRef} className="relative flex items-center gap-3">
        {/* Background bar */}
        <div
          ref={backgroundBarRef}
          className="absolute left-0 h-[7px] rounded-full bg-[#E5D5C3] transition-none"
          style={{
            width: "0px",
            transform: "translateX(0px)",
            zIndex: 0,
          }}
        />

        {/* Dots */}
        {Array.from({ length: itemCount }).map((_, index) => (
          <div
            key={index}
            className={`relative h-[7px] w-[7px] rounded-full transition-colors duration-200 ${
              index === activeDotIndex
                ? "bg-[#BDA280]"
                : "bg-[rgba(189,162,128,0.40)]"
            }`}
            style={{ zIndex: 1 }}
          />
        ))}
      </div>
    </div>
  );
};

export interface AdonisProductRecommendationsCarouselProps {
  items: AdonisProductRecommendationCardProps[];
  hrefForItem?: (
    item: AdonisProductRecommendationCardProps,
    index: number
  ) => string | null | undefined;
  LinkComponent?: (props: {
    to: string;
    children: React.ReactNode;
    className?: string;
  }) => JSX.Element;
  animateText?: boolean;
}

export const AdonisProductRecommendationsCarousel = ({
  items,
  hrefForItem,
  LinkComponent,
}: AdonisProductRecommendationsCarouselProps) => {
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const hasItems = Array.isArray(items) && items.length > 0;
  const isSingle = hasItems && items.length === 1;

  if (!hasItems) return null;
  if (isSingle) {
    // Render a single card without the panel or full-bleed container
    const singleItem = items[0];
    const href = hrefForItem?.(singleItem, 0) ?? singleItem.href ?? undefined;
    return (
      <div className="w-[195px]">
        <AdonisProductRecommendationCard
          {...singleItem}
          href={href ?? undefined}
          LinkComponent={LinkComponent}
        />
      </div>
    );
  }

  return (
    <div
      data-test-id="adonis-product-recommendations-carousel"
      className="relative -mx-4 overflow-hidden p-2"
    >
      <ul
        ref={scrollContainerRef}
        className="-m-2 grid auto-cols-[195px] grid-flow-col items-stretch gap-4 overflow-x-auto px-4 py-2 [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {items.map((card, index) => {
          const href = hrefForItem?.(card, index) ?? card.href ?? undefined;
          // Use a stable key that doesn't change across renders for the same item
          const key = card.imageUrl || card.title || String(index);
          return (
            <li key={key} className="col-span-1 h-full">
              <AdonisProductRecommendationCard
                {...card}
                href={href ?? undefined}
                LinkComponent={LinkComponent}
              />
            </li>
          );
        })}
      </ul>

      <CarouselScrollIndicator
        itemCount={items.length}
        scrollContainerRef={scrollContainerRef}
      />
    </div>
  );
};

export default React.memo(AdonisProductRecommendationsCarousel);
